import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, CATEGORY_META } from "../lib/api";
import { Button } from "../components/ui/button";
import { HealthGauge } from "../components/HealthGauge";
import { SeverityBadge } from "../components/SeverityBadge";
import { IssueDetailDialog } from "../components/IssueDetailDialog";
import { toast } from "sonner";
import {
  ShieldCheck, Zap, Loader2, CheckCircle2, RefreshCw, ChevronRight, AlertTriangle, Radar,
} from "lucide-react";

const Scanner = () => {
  const [phase, setPhase] = useState("idle"); // idle | scanning | done
  const [stages, setStages] = useState([]);
  const [activeStage, setActiveStage] = useState(-1);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const timers = useRef([]);

  useEffect(() => {
    api.get("/scan/stages").then((r) => setStages(r.data.stages)).catch(() => {});
    return () => timers.current.forEach(clearTimeout);
  }, []);

  const startScan = async () => {
    setPhase("scanning");
    setActiveStage(0);
    setResult(null);

    // animate through stages
    const stageList = stages.length ? stages : ["Scanning…"];
    stageList.forEach((_, i) => {
      timers.current.push(setTimeout(() => setActiveStage(i + 1), (i + 1) * 850));
    });

    try {
      const [res] = await Promise.all([
        api.post("/scan"),
        new Promise((r) => setTimeout(r, stageList.length * 850 + 400)),
      ]);
      setResult(res.data);
      setPhase("done");
      toast.success(`Scan complete — ${res.data.total_issues} issues found`);
    } catch (e) {
      toast.error("Scan failed. Please try again.");
      setPhase("idle");
    }
  };

  const openIssue = (issue) => {
    setSelected(issue);
    setDialogOpen(true);
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-6 py-10">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-11 w-11 rounded-xl bg-primary/12 border border-primary/25 grid place-items-center">
          <ShieldCheck className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">System Scanner</h1>
          <p className="text-sm text-muted-foreground">Simulated deep diagnostic across Windows, Office & account layers.</p>
        </div>
      </div>

      {/* IDLE */}
      {phase === "idle" && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="glass rounded-3xl p-8 sm:p-12 mt-8 text-center grid-texture">
          <div className="relative mx-auto h-28 w-28 rounded-full border border-primary/30 bg-primary/10 grid place-items-center glow-blue">
            <Radar className="h-12 w-12 text-primary" />
          </div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold mt-6">Ready to scan this laptop</h2>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">
            We'll inspect {stages.length || 5} diagnostic layers and produce a health score with prioritised fixes.
          </p>
          <Button size="lg" onClick={startScan}
            className="mt-7 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl h-12 px-8"
            data-testid="run-system-scan-btn">
            <Zap className="h-5 w-5 mr-2" /> Start Diagnostic Scan
          </Button>
        </motion.div>
      )}

      {/* SCANNING */}
      {phase === "scanning" && (
        <div className="glass rounded-3xl p-8 mt-8 relative overflow-hidden scan-sweep" data-testid="scan-progress">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-primary mb-6 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Diagnostic in progress
          </p>
          <div className="space-y-3">
            {stages.map((s, i) => {
              const state = i < activeStage ? "done" : i === activeStage ? "active" : "pending";
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className="shrink-0">
                    {state === "done" && <CheckCircle2 className="h-5 w-5 text-emerald-400" />}
                    {state === "active" && <Loader2 className="h-5 w-5 text-primary animate-spin" />}
                    {state === "pending" && <div className="h-5 w-5 rounded-full border-2 border-border" />}
                  </div>
                  <span className={`text-sm ${state === "pending" ? "text-muted-foreground" : "text-foreground"}`}>{s}</span>
                  {state === "done" && <span className="ml-auto text-xs font-mono text-emerald-400">OK</span>}
                </div>
              );
            })}
          </div>
          <div className="mt-6 h-2 rounded-full bg-secondary overflow-hidden">
            <motion.div className="h-full bg-primary"
              animate={{ width: `${Math.min(100, (activeStage / (stages.length || 1)) * 100)}%` }}
              transition={{ duration: 0.5 }} />
          </div>
        </div>
      )}

      {/* RESULTS */}
      <AnimatePresence>
        {phase === "done" && result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* gauge */}
              <div className="glass rounded-3xl p-6 grid place-items-center">
                <HealthGauge score={result.health_score} />
              </div>
              {/* summary */}
              <div className="lg:col-span-2 grid sm:grid-cols-3 gap-4">
                <SummaryCard label="Total Issues" value={result.total_issues} tone="text-foreground" />
                <SummaryCard label="Critical" value={result.critical} tone="text-red-400" />
                <SummaryCard label="Warnings" value={result.warning} tone="text-amber-400" />
                <div className="sm:col-span-3 glass rounded-2xl p-5">
                  <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Category breakdown</p>
                  <div className="space-y-3">
                    {Object.entries(result.category_breakdown).map(([cat, n]) => {
                      const meta = CATEGORY_META[cat] || {};
                      const pct = result.total_issues ? (n / result.total_issues) * 100 : 0;
                      return (
                        <div key={cat}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-foreground/90">{cat}</span>
                            <span className="font-mono text-muted-foreground">{n}</span>
                          </div>
                          <div className="h-2 rounded-full bg-secondary overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: meta.color }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* issue list */}
            <div className="glass rounded-3xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-400" /> Detected issues
                </h3>
                <Button variant="outline" size="sm" onClick={startScan} className="border-border/70" data-testid="rescan-btn">
                  <RefreshCw className="h-4 w-4 mr-1.5" /> Re-scan
                </Button>
              </div>
              <div className="space-y-3">
                {result.issues.map((issue) => {
                  const meta = CATEGORY_META[issue.category] || {};
                  return (
                    <button key={issue.id} onClick={() => openIssue(issue)}
                      className="w-full flex items-center gap-4 rounded-xl border border-border/60 bg-secondary/30 p-4 text-left hover:border-primary/40 hover:-translate-y-0.5 transition-all duration-200"
                      data-testid={`scan-issue-${issue.id}`}>
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: meta.color }} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold truncate">{issue.title}</p>
                          <SeverityBadge severity={issue.severity} />
                        </div>
                        <p className="text-xs font-mono text-muted-foreground mt-1">
                          {issue.error_code} · {issue.component}
                        </p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <IssueDetailDialog issue={selected} open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
};

const SummaryCard = ({ label, value, tone }) => (
  <div className="glass rounded-2xl p-5">
    <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className={`font-heading text-4xl font-extrabold mt-2 ${tone}`}>{value}</p>
  </div>
);

export default Scanner;
